import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useIntl } from 'react-intl';

import { type Artifact } from '../../data/projects/artifacts/state';
import {
  formatAttachmentType,
  formatBytes,
} from '../../data/projects/attachmentFormatting';

import {
  mailAttachmentArtifact,
  type MailAttachmentPreview,
} from './nango-connector-approval';

export function MailAttachmentFootnote({
  attachments,
  status,
  isBusy,
  onOpen,
}: {
  attachments: readonly MailAttachmentPreview[];
  status: 'pending' | 'verified' | 'failed' | 'idle';
  isBusy: boolean;
  onOpen: (artifact: Artifact) => void;
}) {
  const intl = useIntl();
  if (status === 'pending') {
    return (
      <div
        className="border-t border-border px-3 py-2"
        data-testid="nango-mail-approval-attachments-pending"
      >
        <TypographyP2 className="text-sm text-text-secondary">
          {intl.formatMessage({
            defaultMessage: 'Verifying these files.',
            id: 'PEPmUKWIp/',
          })}
        </TypographyP2>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div
        className="border-t border-border px-3 py-2"
        data-testid="nango-mail-approval-attachments-unverified"
      >
        <TypographyP2 className="text-sm text-text-secondary">
          {intl.formatMessage({
            defaultMessage: "Heidi couldn't verify these files.",
            id: 'D37kh9zw8A',
          })}
        </TypographyP2>
      </div>
    );
  }
  if (attachments.length === 0) return null;
  return (
    <div
      className="flex flex-col gap-2 border-t border-border px-3 py-2"
      data-testid="nango-mail-approval-attachments"
    >
      {attachments.map((file, index) => {
        const artifact = file.open ? mailAttachmentArtifact(file.open) : null;
        const size = formatBytes(file.sizeBytes, intl);
        const typeLabel = formatAttachmentType(file.filename, file.mediaType);
        return (
          <div
            key={`${file.filename}-${index}`}
            className="flex items-center justify-between gap-2"
            data-testid="nango-mail-approval-attachment"
          >
            <div className="min-w-0">
              <TypographyP2 className="truncate text-sm text-text-primary">
                {file.filename}
              </TypographyP2>
              <TypographyCaption className="text-text-secondary">
                {size
                  ? intl.formatMessage(
                      { defaultMessage: '{type} · {size}', id: 'gc5nB0UYTG' },
                      { type: typeLabel, size }
                    )
                  : typeLabel}
              </TypographyCaption>
            </div>
            {artifact ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isBusy}
                aria-label={intl.formatMessage(
                  { defaultMessage: 'Open {filename}', id: 'OWXKo1qg2p' },
                  { filename: file.filename }
                )}
                data-testid="nango-mail-approval-attachment-open"
                onClick={() => onOpen(artifact)}
              >
                {intl.formatMessage({ defaultMessage: 'Open', id: 'JfG49wNHKP' })}
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
